import { inject as service } from '@ember/service';
import { mapBy } from '@ember/object/computed';
import Controller from '@ember/controller';
import { Promise } from 'rsvp';
import { isBlank } from '@ember/utils';
import { action, set, computed } from '@ember/object';
import { observes } from '@ember-decorators/object';
import { copy } from 'ember-copy';
import { task, timeout, waitForQueue } from 'ember-concurrency';

const VALIDATION_DEBOUNCE_DELAY = 500;

export default class ActionsUrlParamsEditController extends Controller {
  @service api;
  @service apiErrors;
  @service messageSettings;
  @service router;

  urlParams = [];
  originalParams = [];

  @computed('urlParams.@each.{name,value}', 'originalParams')
  get isDirty() {
    let dirty = false;
    let originalParams = this.get('originalParams');
    let params = this.get('urlParams');
    if (originalParams.length !== params.get('length')) {
      dirty = true;
    } else {
      dirty = params.any((param, index) => {
        return param.name !== originalParams[index].name || param.value !== originalParams[index].value;
      });
    }
    return dirty;
  }

  @mapBy('urlParams', 'name') nameArray;

  @computed('urlParams.@each.{nameError,valueError}')
  get hasErrors() {
    return this.get('urlParams').any((param) => {
      return param.nameError || param.valueError;
    });
  }

  @observes('urlParams.@each.{name,value}')
  paramDidChange() {
    this.get('fetchErrors').perform();
  }

  fetchErrors = task(
    {
      restartable: true,
    },
    async () => {
      await waitForQueue('actions');
      await timeout(VALIDATION_DEBOUNCE_DELAY);

      let promise = new Promise((resolve) => {
        this.get('urlParams').forEach((param) => {
          set(param, 'nameError', null);
          set(param, 'valueError', null);
          if (isBlank(param.name)) {
            set(param, 'nameError', "Name can't be blank!");
          }
          if (isBlank(param.value)) {
            if (isBlank(param.name)) {
              // Clear out the nameError also because if both are blank we won't show the errors
              set(param, 'nameError', null);
            } else {
              set(param, 'valueError', "Value can't be blank!");
            }
          }

          // Check for dupes
          let nameArray = this.get('nameArray');
          let withoutArray = nameArray.without(param.name);
          if (nameArray.length > withoutArray.length + 1 && !isBlank(param.name)) {
            set(param, 'nameError', 'Parameter already exists with this name.');
          }

          // Attempts to use liquid in the name
          if (param.name.includes('{{')) {
            set(param, 'nameError', "Name can't contain Liquid!");
          }
        });
        resolve(this.get('urlParams'));
      });

      let result = await promise;

      this.set('urlParams', result);
    },
  );

  saveParams = task(
    {
      drop: true,
    },
    async () => {
      let toRemove = [];
      let urlParams = this.get('urlParams');
      urlParams.forEach((param) => {
        if (isBlank(param.name) && isBlank(param.value)) {
          toRemove.addObject(param);
        }
      });
      urlParams.removeObjects(toRemove);

      let data = { url_params: urlParams };
      await this.get('api').post('url_params', { data });
      this.set('originalParams', copy(data.url_params, true));

      this.router.transitionTo('actions.url_params.index');
    },
  );

  @action
  addParam() {
    this.get('urlParams').pushObject({ name: '', value: '' });
  }

  @action
  removeParam(param) {
    this.get('urlParams').removeObject(param);
  }

  @action
  resetParams() {
    this.set('urlParams', copy(this.get('originalParams'), true));
    this.router.transitionTo('actions.url_params.index');
  }
}
